/**
 * Taula d'assignatures d'examenselectivitat ↔ apren.ai. Dades pures (sense imports) perquè la puguin llegir
 * tant el client (utils/aprenAi.js, utils/profes.js) com els scripts del servidor (server/scripts/seo/generateSeo.js,
 * amb `await import()`).
 */

/** subject_path d'examenselectivitat → slug de l'assignatura a apren.ai (el número del final és el node) */
export const SUBJECTS = {
  biologia: 'biologia-891108', fisica: 'fisica-891109', alemany: 'aleman-891110', frances: 'frances-891111',
  angles: 'ingles-891112', italia: 'italiano-891113', matematiques: 'matematicas-ii-891114', quimica: 'quimica-891116',
  'economia-de-l-empresa': 'economia-de-la-empresa-5-891117', 'dibuix-artistic': 'dibujo-artistico-891118',
  'historia-de-l-art': 'historia-del-arte-891119', grec: 'griego-ii-891120',
  'historia-de-la-filosofia': 'historia-de-la-filosofia-891121', llati: 'latin-ii-891122', historia: 'historia-de-espana-891123',
  geografia: 'geografia-891125', 'dibuix-tecnic': 'dibujo-tecnico-ii-891126', disseny: 'diseno-891127',
  'fonaments-de-les-arts': 'fundamentos-artisticos-891128', 'llengua-castellana': 'lengua-castellana-y-literatura-ii-891129',
  'matematiques-cs': 'matematicas-ccss-ii-891130', 'analisi-musical': 'analisis-musical-891131',
  'literatura-catalana': 'literatura-catalana-891132', 'ciencies-de-la-terra': 'ctma-891133',
  'llengua-catalana': 'lengua-catalana-y-literatura-ii-2-891134', 'literatura-castellana': 'literatura-castellana-891135',
  'tecnologia-industrial': 'tecnologia-industrial-891136', 'arts-esceniques': 'artes-escenicas-904119',
  'ciencies-generals': 'ciencias-generales-904120', 'cor-tecnica-vocal': 'coro-y-tecnica-vocal-904121',
  'dibuix-tecnic-aplicat-arts-disseny': 'dibujo-tecnico-artes-904122',
  'historia-de-la-musica-i-de-la-dansa': 'historia-musica-danza-904123',
  'literatura-dramatica': 'literatura-dramatica-904124', 'moviments-culturals-i-artistics': 'movimientos-culturales-y-artisticos-904125',
  'teniques-d-expressio-graficoplastiques': 'expresion-grafico-plastica-904126',
};

/** subject_path → subject_name (el de la taula subjects d'examenselectivitat, el que surt a les URL /selectivitat/<nom>) */
export const SUBJECT_NAMES = {
  angles: 'Anglès', historia: 'Història', 'llengua-castellana': 'Llengua castellana', 'llengua-catalana': 'Llengua catalana',
  biologia: 'Biologia', 'ciencies-de-la-terra': 'Ciències de la terra', 'dibuix-tecnic': 'Dibuix tècnic', fisica: 'Física',
  quimica: 'Química', matematiques: 'Matemàtiques', 'tecnologia-industrial': 'Tecnologia industrial',
  'analisi-musical': 'Anàlisi musical', 'dibuix-artistic': 'Dibuix artístic', disseny: 'Disseny',
  'fonaments-de-les-arts': 'Fonaments de les arts', 'economia-de-l-empresa': "Economia de l'empresa", geografia: 'Geografia',
  grec: 'Grec', 'historia-de-la-filosofia': 'Història de la filosofia', 'literatura-castellana': 'Literatura castellana',
  'literatura-catalana': 'Literatura catalana', llati: 'Llatí', 'matematiques-cs': 'Matemàtiques CC.SS.',
  'historia-de-l-art': "Història de l'art", italia: 'Italià', frances: 'Francès', alemany: 'Alemany',
  'arts-esceniques': 'Arts Escèniques', 'ciencies-generals': 'Ciències Generals',
  'historia-de-la-musica-i-de-la-dansa': 'Història de la Música i de la Dansa',
  'moviments-culturals-i-artistics': 'Moviments culturals i artístics', 'literatura-dramatica': 'Literatura dramàtica',
  'dibuix-tecnic-aplicat-arts-disseny': 'Dibuix Tècnic Aplicat a les Arts Plàstiques i el Disseny',
  'teniques-d-expressio-graficoplastiques': "Tècniques d'Expressió Graficoplàstiques", 'cor-tecnica-vocal': 'Cor i Tècnica Vocal',
};

/** Nom sense accents, majúscules, apòstrofs ni puntuació: «Matemàtiques CC.SS.» → «matematiquesccss» */
export function normalizeSubjectName(name) {
  let s = String(name || '');
  try {
    s = decodeURIComponent(s);
  } catch (e) {
    // ja venia descodificat
  }
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

// Altres noms amb què surt una assignatura (p. ex. a la descripció de les comandes: «Quadern Matemàtiques C.S. Selectivitat»)
const ALIASES = { matematiquescs: 'matematiques-cs', matematiquesccss: 'matematiques-cs', ciencieslaterra: 'ciencies-de-la-terra' };

const BY_NAME = Object.entries(SUBJECT_NAMES).reduce((m, [path, name]) => {
  m[normalizeSubjectName(name)] = path;
  return m;
}, { ...ALIASES });

/** subject_name («Química», també codificat a la URL) → subject_path («quimica»), o null */
export function subjectPathFromName(name) {
  if (!name) return null;
  if (SUBJECT_NAMES[name]) return name; // ja era un subject_path
  return BY_NAME[normalizeSubjectName(name)] || null;
}

/** Node de l'assignatura a apren.ai (el número del final del slug) */
export function nodeIdOf(subjectPath) {
  const slug = subjectPath && SUBJECTS[subjectPath];
  const m = slug && slug.match(/-(\d+)$/);
  return m ? Number(m[1]) : null;
}

/** Al revés: node d'apren.ai → subject_path d'examenselectivitat */
export function subjectPathFromNodeId(nodeId) {
  const n = Number(nodeId);
  return Object.keys(SUBJECTS).find(p => nodeIdOf(p) === n) || null;
}
